import { defineStore } from 'pinia'
import type { RolePermission, PermissionMap, PermissionModule, PermissionAction } from '~/types/permission'
import { translateSupabaseError } from '~/utils/supabase-error'

export type UserRole = 'doctor' | 'nurse' | 'administrative' | 'receptionist' | 'collection_manager' | 'accountant' | 'embryologist' | 'owner'

export interface FullRolePermission {
  id:         string
  role:       UserRole
  module:     PermissionModule
  can_read:   boolean
  can_create: boolean
  can_update: boolean
  can_delete: boolean
}

const STALE_MS = 30 * 60_000  // 30 min

interface PermissionsState {
  permissions:          PermissionMap | null
  rolePermissions:      FullRolePermission[]
  loading:              boolean
  saving:               boolean
  error:                string | null
  lastFetchPermissions: number | null
  lastFetchAll:         number | null
}

/** Lista canónica de los 29 módulos — debe coincidir con types/permission.ts
 *  y con seed_default_permissions (migraciones 011, 020, 023, 050, 060, 062,
 *  072, 099, 115, 1126 — 'pharmacy', Haus F3). */
export const ALL_PERMISSION_MODULES: PermissionModule[] = [
  'dashboard', 'patients', 'appointments', 'appointment_requests',
  'ehr', 'notes', 'prescriptions', 'surgeries', 'procedures',
  'triage', 'documents', 'bulletins', 'payments', 'billing',
  'consultation_billing', 'inventory', 'settings', 'activity_logs',
  'expenses', 'vaccines_pending', 'informes', 'assisted_reproduction',
  'pos', 'sucursales', 'spaces', 'external_doctors',
  'doctor_accounts', 'pricing', 'pharmacy',
  'patient_accounts', 'collections',
]

/** Mapa vacio — todos los permisos en FALSE (para estado inicial / error) */
function emptyMap(): PermissionMap {
  return Object.fromEntries(
    ALL_PERMISSION_MODULES.map(m => [m, { can_read: false, can_create: false, can_update: false, can_delete: false }])
  ) as PermissionMap
}

export const usePermissionsStore = defineStore('permissions', {
  state: (): PermissionsState => ({
    permissions:          null,
    rolePermissions:      [],
    loading:              false,
    saving:               false,
    error:                null,
    lastFetchPermissions: null,
    lastFetchAll:         null,
  }),

  getters: {
    loaded: (s) => s.permissions !== null,
  },

  actions: {
    async fetchPermissions(force = false) {
      if (!force && this.lastFetchPermissions && Date.now() - this.lastFetchPermissions < STALE_MS && this.permissions !== null) return
      const supabase = useSupabaseClient()
      this.loading   = true
      this.error     = null
      try {
        const { data, error } = await supabase.rpc('get_my_permissions')
        if (error) throw error

        const map = emptyMap()
        for (const row of (data as RolePermission[])) {
          map[row.module as PermissionModule] = {
            can_read:   row.can_read,
            can_create: row.can_create,
            can_update: row.can_update,
            can_delete: row.can_delete,
          }
        }
        this.permissions = map
        this.lastFetchPermissions = Date.now()
      } catch (e: any) {
        console.error(e)
        this.error       = 'Error al cargar permisos'
        this.permissions = null  // keep loaded=false so middleware retries on next navigation
      } finally {
        this.loading = false
      }
    },

    async fetchAllPermissions(force = false) {
      if (!force && this.lastFetchAll && Date.now() - this.lastFetchAll < STALE_MS && this.rolePermissions.length > 0) return
      const supabase = useSupabaseClient()
      this.loading   = true
      this.error     = null
      try {
        const { data, error } = await supabase
          .from('role_permissions')
          .select('id, role, module, can_read, can_create, can_update, can_delete')
          .in('role', ['doctor', 'assistant_doctor', 'nurse', 'administrative', 'receptionist', 'collection_manager', 'accountant', 'owner'])
          .order('role')
          .order('module')
        if (error) throw error
        this.rolePermissions = data as FullRolePermission[]
        this.lastFetchAll = Date.now()
      } catch (e: any) {
        console.error(e)
        this.error           = 'Error al cargar permisos'
        this.rolePermissions = []
      } finally {
        this.loading = false
      }
    },

    async updatePermission(
      id: string,
      field: 'can_read' | 'can_create' | 'can_update' | 'can_delete',
      value: boolean,
    ) {
      const supabase = useSupabaseClient()
      this.saving    = true
      try {
        const { error } = await supabase
          .from('role_permissions')
          .update({ [field]: value })
          .eq('id', id)
        if (error) throw error
        const idx = this.rolePermissions.findIndex(p => p.id === id)
        if (idx !== -1) this.rolePermissions[idx][field] = value
        this.lastFetchPermissions = null  // invalidar caché del usuario actual
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally {
        this.saving = false
      }
    },

    async resetToDefaults() {
      const supabase  = useSupabaseClient()
      const authStore = useAuthStore()
      this.saving     = true
      this.error      = null
      try {
        const { error } = await supabase.rpc('seed_default_permissions', {
          p_clinic_id: authStore.clinicId,
        })
        if (error) throw error
        await this.fetchAllPermissions(true)
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally {
        this.saving = false
      }
    },

    can(module: PermissionModule, action: PermissionAction = 'read'): boolean {
      if (!this.permissions) return false
      const perm = this.permissions[module]
      if (!perm) return false
      return perm[`can_${action}`]
    },

    clearPermissions() {
      this.permissions     = null
      this.rolePermissions = []
      this.error           = null
    },
  },
})
